import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Panel, Loader } from "@/components/portal/Shared";
import { toast } from "sonner";
import { Leaf, Search, ShieldAlert, ChevronLeft, Bookmark, BookMarked, Layers, ScrollText, Landmark, HeartPulse, Sparkles, ArrowRight, ExternalLink } from "lucide-react";
import WellnessAssistant from "./WellnessAssistant";

const IMG_BASE = process.env.REACT_APP_BACKEND_URL || "";
const plantImg = (p) => (p && p.image_url ? IMG_BASE + p.image_url : null);

const SOURCE_STYLE = {
  TRIBAL: "bg-[#E7F6EC] text-[#166534]",
  ETHNOBOTANICAL: "bg-[#EDE9FE] text-[#5B21B6]",
  HISTORICAL: "bg-[#FEF3C7] text-[#92400E]",
  ORAL: "bg-[#E0F2FE] text-[#075985]",
};
const SOURCE_LABEL = { TRIBAL: "Tribal source", ETHNOBOTANICAL: "Ethnobotanical source", HISTORICAL: "Historical source", ORAL: "Oral tradition" };
const field = "w-full rounded-lg border border-[#E4DCD3] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50";

const SafetyNotice = ({ text }) => (
  <div className="rounded-xl bg-[#FFF7ED] border border-[#F59E0B]/40 p-4 mb-5 flex items-start gap-3" data-testid="remedy-safety-notice">
    <ShieldAlert size={18} className="text-[#B45309] shrink-0 mt-0.5" />
    <div>
      <div className="font-bold text-[#92400E] text-sm">Traditional use ≠ medical claim</div>
      <p className="text-xs text-[#7C2D12] leading-relaxed mt-1">{text}</p>
    </div>
  </div>
);

function Layer({ icon: Icon, title, children }) {
  return (
    <div className="rounded-xl border border-[#E4DCD3] bg-white p-4">
      <div className="flex items-center gap-2 text-[#59288E] font-serif font-semibold"><Icon size={16} /> {title}</div>
      <div className="text-sm text-[#1A171D] mt-2 leading-relaxed">{children}</div>
    </div>
  );
}

function FeaturedPlantHero({ plant, onView }) {
  if (!plant || !plant.image_url) return null;
  return (
    <button onClick={() => onView(plant)} data-testid="featured-plant-hero"
      className="group relative block w-full overflow-hidden rounded-3xl mb-6 text-left focus:outline-none focus:ring-2 focus:ring-[#D4AF37]">
      <img src={plantImg(plant)} alt={plant.image_alt || plant.common_name}
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#123524]/92 via-[#123524]/70 to-transparent" />
      <div className="relative z-10 p-7 sm:p-10 max-w-2xl min-h-[240px] flex flex-col justify-end">
        <div className="inline-flex items-center gap-1.5 self-start rounded-full bg-[#D4AF37] text-[#180D24] px-3 py-1 text-[11px] font-bold uppercase tracking-wide mb-3">
          <Sparkles size={13} /> Featured remedy
        </div>
        <h3 data-testid="featured-plant-name" className="font-serif text-3xl sm:text-4xl font-bold text-white leading-tight">{plant.common_name}</h3>
        <div className="text-sm text-[#B9E4C9] font-mono italic mt-1.5">{plant.scientific_name}</div>
        <p className="text-sm text-[#E4F2E9] mt-2 line-clamp-2">{plant.use_summary}</p>
        <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-white group-hover:gap-2.5 transition-all">
          Explore this plant <ArrowRight size={16} />
        </span>
      </div>
    </button>
  );
}

function PlantDetail({ p, saved, notes, onBack, onToggle, onNotes }) {
  const [note, setNote] = useState(notes || "");
  return (
    <div data-testid="remedy-detail">
      <button onClick={onBack} data-testid="remedy-back-btn" className="inline-flex items-center gap-1.5 text-sm text-[#59288E] font-semibold mb-4 hover:underline"><ChevronLeft size={16} /> Back to plants</button>
      <Panel className="p-6">
        {plantImg(p) && (
          <div className="relative -mx-6 -mt-6 mb-5 h-56 sm:h-72 overflow-hidden rounded-t-2xl">
            <img src={plantImg(p)} alt={p.image_alt || p.common_name} className="absolute inset-0 h-full w-full object-cover" data-testid="remedy-detail-image" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/45 to-transparent" />
            {p.image_credit && (
              <div className="absolute bottom-2 right-3 text-[10px] text-white/85 bg-black/40 rounded px-2 py-0.5">
                {p.image_credit} · {p.image_license}
              </div>
            )}
          </div>
        )}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${SOURCE_STYLE[p.source_type]}`}>{SOURCE_LABEL[p.source_type]}</span>
            <h2 className="font-serif text-3xl font-bold text-[#180D24] mt-2">{p.common_name}</h2>
            <div className="text-sm text-[#8F7220] font-mono italic mt-1">{p.scientific_name}{p.native_name ? ` · ${p.native_name}` : ""} · {p.plant_part}</div>
          </div>
          <button onClick={() => onToggle(p)} data-testid="remedy-save-btn"
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold transition-colors ${saved ? "bg-[#166534] text-white" : "bg-[#D4AF37] text-[#180D24] hover:bg-[#E7C965]"}`}>
            {saved ? <BookMarked size={15} /> : <Bookmark size={15} />} {saved ? "In my library" : "Save to my plant library"}
          </button>
        </div>

        <p className="mt-4 text-[#1A171D] leading-relaxed">{p.use_summary}</p>

        <div className="grid md:grid-cols-2 gap-3 mt-5">
          <Layer icon={ScrollText} title="Cultural knowledge">{p.tribal_uses.map((t) => t.nation).join(", ")} — recorded traditional use of this plant.</Layer>
          <Layer icon={Landmark} title="Historical record">{SOURCE_LABEL[p.source_type]}: {p.source}. Evidence status: <span className="font-semibold">{p.evidence_status}</span>.</Layer>
          <Layer icon={Leaf} title="Botanical identification">{p.scientific_name} · part used: {p.plant_part}. Region: {p.region}. {p.preparation}
            {p.image_source_url ? <a href={p.image_source_url} target="_blank" rel="noreferrer" data-testid="remedy-image-source" className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#59288E] hover:underline"><ExternalLink size={12} /> Image source & license</a> : null}
          </Layer>
          <Layer icon={HeartPulse} title="Modern safety information">Toxicity: <span className="font-semibold">{p.toxicity_status}</span>. {p.safety_notes}</Layer>
        </div>

        <div className="mt-5" data-testid="compare-tribal-uses">
          <h3 className="font-serif text-lg font-semibold text-[#180D24] mb-1 flex items-center gap-2"><Layers size={17} className="text-[#59288E]" /> Compare tribal uses</h3>
          <p className="text-xs text-[#756D7E] mb-3">Indigenous botanical knowledge was never one universal system — different nations held different traditions.</p>
          <div className="space-y-2">
            {p.tribal_uses.map((t, k) => (
              <div key={k} className="rounded-lg border border-[#E4DCD3] bg-[#FBF7FF] p-3">
                <div className="text-sm font-semibold text-[#3F1E68]">{t.nation}</div>
                <div className="text-sm text-[#1A171D]">Recorded use: {t.use}</div>
                {t.preparation ? <div className="text-xs text-[#8F7220] mt-0.5">Preparation: {t.preparation}</div> : null}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-5">
          <div className="text-xs font-bold uppercase tracking-wide text-[#8F7220] mb-1">My private notes</div>
          <textarea className={field} rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add your own notes (never alters the historical entry)…" data-testid="remedy-notes-input" />
          <button onClick={() => onNotes(p.id, note)} className="mt-2 rounded-full bg-[#2A1442] text-white px-4 py-2 text-xs font-bold hover:bg-[#482276]">Save notes</button>
        </div>
      </Panel>
    </div>
  );
}

export default function TribalRemedies({ navPayload, onNavigate }) {
  const [data, setData] = useState(null);
  const [savedMap, setSavedMap] = useState({});
  const [detail, setDetail] = useState(null);
  const [q, setQ] = useState("");
  const [nation, setNation] = useState("");
  const [region, setRegion] = useState("");
  const [fn, setFn] = useState("");
  const [tab, setTab] = useState("browse");

  const load = () => api.get("/remedies").then((r) => { setData(r.data); setSavedMap(r.data.saved); });
  useEffect(() => { load(); }, []);

  const toggle = async (p) => {
    const { data: res } = await api.post(`/remedies/${p.id}/save`);
    setSavedMap((m) => { const n = { ...m }; if (res.saved) n[p.id] = ""; else delete n[p.id]; return n; });
    toast.success(res.saved ? "Saved to your plant library" : "Removed from library");
  };
  const saveNotes = async (id, notes) => { await api.patch(`/remedies/${id}/notes`, { notes }); setSavedMap((m) => ({ ...m, [id]: notes })); toast.success("Notes saved"); };

  const featured = useMemo(() => {
    if (!data) return null;
    const withPhoto = data.plants.filter((p) => p.image_url);
    if (!withPhoto.length) return null;
    return withPhoto[Math.floor(Math.random() * withPhoto.length)];
  }, [data]);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.plants.filter((p) => {
      if (nation && !p.tribal_uses.some((t) => t.nation === nation)) return false;
      if (region && p.region !== region) return false;
      if (fn && !p.functions.includes(fn)) return false;
      if (q) {
        const hay = `${p.common_name} ${p.scientific_name} ${p.use_summary} ${p.tribal_uses.map((t) => t.nation).join(" ")}`.toLowerCase();
        if (!hay.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [data, nation, region, fn, q]);

  useEffect(() => {
    if (!data || !navPayload) return;
    if (navPayload.plantId) {
      const p = data.plants.find((x) => x.id === navPayload.plantId);
      if (p) { setDetail(p); setTab("browse"); }
    } else if (navPayload.search) {
      const s = navPayload.search.toLowerCase();
      const exact = data.plants.find((x) => x.common_name.toLowerCase().includes(s));
      if (exact) setDetail(exact); else { setQ(navPayload.search); setTab("browse"); }
    }
  }, [data, navPayload]);

  if (!data) return <Loader />;
  const savedPlants = data.plants.filter((p) => p.id in savedMap);
  if (detail) return (<div><SafetyNotice text={data.safety_notice} /><PlantDetail p={detail} saved={detail.id in savedMap} notes={savedMap[detail.id]} onBack={() => setDetail(null)} onToggle={toggle} onNotes={saveNotes} /></div>);

  return (
    <div data-testid="module-tribal-remedies">
      <PageHeader eyebrow="Tribal Remedies" title="Tribal Remedies"
        subtitle="Explore traditional plant knowledge by Nation, region, plant and historical use — presented as documented cultural knowledge, with source-quality badges and safety information." />
      <SafetyNotice text={data.safety_notice} />

      <div className="flex items-center gap-2 mb-5 flex-wrap">
        {[["browse", "Explore plants"], ["wellness", "Wellness Research"], ["library", `My plant library (${savedPlants.length})`]].map(([id, label]) => (
          <button key={id} data-testid={`remedies-tab-${id}`} onClick={() => setTab(id)}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === id ? "bg-[#2A1442] text-white" : "bg-white border border-[#E4DCD3] text-[#59288E] hover:border-[#D4AF37]"}`}>{label}</button>
        ))}
      </div>

      {tab === "wellness" && (
        <WellnessAssistant meta={data.meta} plants={data.plants} onNavigate={onNavigate} />
      )}

      {tab === "browse" && (
        <>
          <FeaturedPlantHero plant={featured} onView={setDetail} />
          <Panel className="p-4 mb-5">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="relative">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#756D7E]" />
                <input className={`${field} pl-9`} placeholder="Search a plant…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="remedy-search-input" />
              </div>
              <select className={field} value={nation} onChange={(e) => setNation(e.target.value)} data-testid="remedy-nation-filter"><option value="">All Nations</option>{data.meta.nations.map((t) => <option key={t} value={t}>{t}</option>)}</select>
              <select className={field} value={region} onChange={(e) => setRegion(e.target.value)}><option value="">All Regions</option>{data.meta.regions.map((t) => <option key={t} value={t}>{t}</option>)}</select>
              <select className={field} value={fn} onChange={(e) => setFn(e.target.value)} data-testid="remedy-function-filter"><option value="">All traditional uses</option>{data.meta.functions.map((t) => <option key={t} value={t}>{t}</option>)}</select>
            </div>
          </Panel>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="remedy-grid">
            {filtered.map((p) => (
              <button key={p.id} onClick={() => setDetail(p)} data-testid={`remedy-card-${p.id}`} className="text-left rounded-2xl border border-[#E4DCD3] bg-white overflow-hidden hover:border-[#D4AF37] hover:shadow-md transition-all">
                {plantImg(p) ? <div className="h-40 overflow-hidden"><img src={plantImg(p)} alt={p.image_alt || p.common_name} className="h-full w-full object-cover" loading="lazy" /></div> : null}
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${SOURCE_STYLE[p.source_type]}`}>{SOURCE_LABEL[p.source_type]}</span>
                    {p.id in savedMap ? <BookMarked size={15} className="text-[#166534]" /> : null}
                  </div>
                  <h3 className="font-serif text-xl font-semibold text-[#180D24] mt-2">{p.common_name}</h3>
                  <div className="text-xs text-[#8F7220] font-mono italic mt-1">{p.scientific_name}</div>
                  <p className="text-sm text-[#756D7E] mt-2 line-clamp-2">{p.use_summary}</p>
                  <div className="mt-2 flex flex-wrap gap-1">{p.functions.map((f) => <span key={f} className="text-[10px] bg-[#F3EEFA] text-[#59288E] px-2 py-0.5 rounded-full">{f}</span>)}</div>
                </div>
              </button>
            ))}
            {filtered.length === 0 ? <div className="col-span-full text-center py-12 text-[#756D7E]">No plants match those filters.</div> : null}
          </div>
        </>
      )}

      {tab === "library" && (
        <div data-testid="plant-library-view">
          {savedPlants.length === 0 ? <div className="text-center py-14 text-[#756D7E]">Your plant library is empty. Save plants to build your collection.</div> : null}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {savedPlants.map((p) => (
              <button key={p.id} onClick={() => setDetail(p)} className="text-left rounded-2xl border border-[#E4DCD3] bg-white p-5 hover:border-[#D4AF37] transition-all">
                <h3 className="font-serif text-lg font-semibold text-[#180D24]">{p.common_name}</h3>
                <div className="text-xs text-[#8F7220] font-mono italic mt-1">{p.scientific_name}</div>
                {savedMap[p.id] ? <p className="text-xs text-[#756D7E] mt-2 italic">Your note: {savedMap[p.id]}</p> : null}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
